
import { useState } from "react";
import { Control, useFieldArray } from "react-hook-form";
import { WorkoutType } from "@/models/WorkoutType";

interface UseSubscriptionManagementProps {
  control: Control<any>;
  workoutTypes: WorkoutType[];
}

export const useSubscriptionManagement = ({ control, workoutTypes }: UseSubscriptionManagementProps) => {
  const [selectedWorkoutTypeId, setSelectedWorkoutTypeId] = useState<string>("");
  const [openSections, setOpenSections] = useState<{ [key: number]: boolean }>({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subscriptions"
  });

  // Filter out workout types that are already added
  const getAvailableWorkoutTypes = () => {
    const usedWorkoutTypeIds = fields.map((field: any) => field.workoutTypeId);
    return workoutTypes.filter(wt => !usedWorkoutTypeIds.includes(wt.id));
  };

  const handleAddSubscription = (workoutTypeId?: string) => {
    const typeId = workoutTypeId || selectedWorkoutTypeId;
    if (typeId) {
      const selectedWorkoutType = workoutTypes.find(wt => wt.id === typeId);
      if (selectedWorkoutType) {
        const newIndex = fields.length;
        append({
          workoutTypeId: typeId,
          sessionsPerMonth: '', // Remove default value
          totalPrice: '' // Remove default value
        });
        setSelectedWorkoutTypeId("");
        // Auto-expand the newly added subscription
        setOpenSections(prev => ({ ...prev, [newIndex]: true }));
      }
    }
  };

  // Auto-add subscription when workout type is selected
  const handleWorkoutTypeSelect = (workoutTypeId: string) => {
    setSelectedWorkoutTypeId(workoutTypeId);
    if (workoutTypeId) {
      handleAddSubscription(workoutTypeId);
    }
  };

  const handleRemoveSubscription = (index: number) => {
    remove(index);
    // Update open sections after removal
    const newOpenSections: { [key: number]: boolean } = {};
    Object.keys(openSections).forEach(key => {
      const keyIndex = parseInt(key);
      if (keyIndex < index) {
        newOpenSections[keyIndex] = openSections[keyIndex];
      } else if (keyIndex > index) {
        newOpenSections[keyIndex - 1] = openSections[keyIndex];
      }
    });
    setOpenSections(newOpenSections);
  };

  const toggleSection = (index: number) => {
    setOpenSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || 'Unknown Workout Type';
  };

  const getWorkoutTypeColor = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.color || '#3498db';
  };

  return {
    fields,
    selectedWorkoutTypeId,
    setSelectedWorkoutTypeId,
    openSections,
    availableWorkoutTypes: getAvailableWorkoutTypes(),
    handleAddSubscription,
    handleWorkoutTypeSelect,
    handleRemoveSubscription,
    toggleSection,
    getWorkoutTypeName,
    getWorkoutTypeColor
  };
};
